<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { CheckoutStages, CheckoutStep } from '../../services/models/checkout.models';
import { HeadingLevel } from '../accordion/accordionSchema';
import Icon from '../icon/icon.vue';
import { useCheckoutStateStore } from './stores/checkoutStateStore';

const OFFSET_TO_SHOW_PREVIOUSLY_COMPLETED_STEP = 300 as const;

const props = defineProps<{
  headingLevel?: HeadingLevel;
  headingClass?: string;
  containerClass?: string;
  stepClass?: string;
  steps: Array<CheckoutStep>;
  completedSteps?: Array<CheckoutStep>;
  activeStep: CheckoutStep;
}>();

const checkoutStateStore = useCheckoutStateStore();
const stepElements = ref();

watch(
  () => props.activeStep,
  (newActiveStep) => scrollToStep(newActiveStep)
);

const headingLevelWithDefault = computed(() => props.headingLevel ?? 'h2');

const scrollToStep = (step: CheckoutStep, scrollBehavior: ScrollBehavior = 'smooth') => {
  const stepElement = stepElements.value[props.steps.indexOf(step)] as HTMLElement;

  if (stepElement) {
    nextTick(() =>
      window.scrollTo({
        top: stepElement.offsetTop - OFFSET_TO_SHOW_PREVIOUSLY_COMPLETED_STEP,
        behavior: scrollBehavior,
      })
    );
  }
};

const displayEditButton = (step: CheckoutStep) => {
  return props.activeStep != step && props.completedSteps && props.completedSteps.includes(step);
};

const buttonHeading = (numberIndicator: number, step: CheckoutStep) => {
  return `${numberIndicator}. ${step}`;
};

const handleClick = async (step: CheckoutStep) => {
  await checkoutStateStore.goToStep(step);
};

const goToShoppingCart = async () => {
  await checkoutStateStore.goToStage(CheckoutStages.ShoppingCart);
};

/**
 * Scrolls to the active step after goToStep() finishes validating and loading.
 */
watch(
  () => checkoutStateStore.isLoading,
  (isLoading, wasLoading) => {
    if (wasLoading && !isLoading) {
      scrollToStep(props.activeStep, 'auto');
    }
  }
);
</script>

<template>
  <div :class="props.containerClass">
    <div class="layout-new-checkout__primary-content">
      <div
        class="feedback-message"
        v-if="checkoutStateStore.newItemsAfterVerification?.length"
      >
        <div class="u-text-left">
          <Icon type="info" size="tiny" /> Handlekurven din har produkter fra en tidligere handel.
          Sjekk sammendrag eller
          <button class="btn--inline" @click.prevent="goToShoppingCart">
            endre i handlekurven
          </button>
        </div>
      </div>
      <div
        v-for="(step, index) in props.steps"
        ref="stepElements"
        :key="index"
        class="checkout-step-selector"
        :class="props.stepClass"
      >
        <component
          :is="headingLevelWithDefault"
          :class="[
            props.headingClass,
            {
              'checkout-step-selector__heading--padded': !displayEditButton(step),
            },
          ]"
        >
          <span
            :class="{
              'checkout-step-selector__heading-content--padded': displayEditButton(step),
            }"
            >{{ buttonHeading(index + 1, step) }}</span
          >
          <button
            v-if="displayEditButton(step)"
            class="checkout-step-selector__edit-step checkout-step-selector__activator"
            @click="handleClick(step)"
          >
            <Icon type="pen" size="checkout-edit"></Icon>
            <span class="checkout-step-selector__edit-text">{{ $t('change') }}</span>
          </button>
        </component>
        <div v-if="props.activeStep" class="checkout-step-selector__content">
          <slot :name="step"></slot>
        </div>
      </div>
    </div>
  </div>
</template>
